import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { TrendingUp, DollarSign, Calendar, ChevronRight, MessageSquare, History, Package, Languages, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { key: 'new', label: 'New Leads', color: 'bg-blue-500', textColor: 'text-blue-600', bgLight: 'bg-blue-50' },
  { key: 'contacted', label: 'Contacted', color: 'bg-purple-500', textColor: 'text-purple-600', bgLight: 'bg-purple-50' },
  { key: 'negotiation', label: 'Negotiation', color: 'bg-amber-500', textColor: 'text-amber-600', bgLight: 'bg-amber-50' },
  { key: 'proposal', label: 'Proposal Sent', color: 'bg-orange-500', textColor: 'text-orange-600', bgLight: 'bg-orange-50' },
  { key: 'closed_won', label: 'Closed Won', color: 'bg-green-500', textColor: 'text-green-600', bgLight: 'bg-green-50' },
  { key: 'closed_lost', label: 'Closed Lost', color: 'bg-slate-500', textColor: 'text-slate-600', bgLight: 'bg-slate-50' }
];

export default function Leads() {
  const { language } = useLanguage();
  const [leads, setLeads] = useState([]);
  const [user, setUser] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [newStage, setNewStage] = useState('');
  const [stageNote, setStageNote] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Translation state for messages
  const [translatedMessages, setTranslatedMessages] = useState({});
  const [isTranslatingMessages, setIsTranslatingMessages] = useState({});

  const fetchLeads = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    }
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    fetchLeads();
  }, [fetchLeads]);

  const handleUpdateStage = async () => {
    if (!newStage || !selectedLead) return;
    
    setIsUpdating(true);
    try {
      await axios.patch(`${API}/leads/${selectedLead.id}/stage`, {
        stage: newStage,
        notes: stageNote || undefined
      });
      toast.success(`Lead moved to ${STAGES.find(s => s.key === newStage)?.label}`);
      setShowDetailDialog(false);
      setNewStage('');
      setStageNote('');
      await fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    } finally {
      setIsUpdating(false);
    }
  };

  const openLeadDetail = async (lead) => {
    try {
      const response = await axios.get(`${API}/leads/${lead.id}`);
      setSelectedLead(response.data);
      // Reset to empty so user must select a new stage
      setNewStage('');
      setStageNote('');
      setShowDetailDialog(true);
    } catch (error) {
      // Fallback to using the lead from list
      setSelectedLead(lead);
      setNewStage('');
      setStageNote('');
      setShowDetailDialog(true);
    }
  };

  // Memoize grouped leads to prevent recalculation on every render
  const groupedLeads = useMemo(() => {
    return STAGES.reduce((acc, stage) => {
      acc[stage.key] = leads.filter(lead => lead.stage === stage.key);
      return acc;
    }, {});
  }, [leads]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStageInfo = (stageKey) => STAGES.find(s => s.key === stageKey) || STAGES[0];

  // Translate messages when language changes or lead is selected
  useEffect(() => {
    const translateMessages = async () => {
      if (!selectedLead?.conversation_history || selectedLead.conversation_history.length === 0) return;
      
      const currentLang = language || 'en';
      if (currentLang === 'en') {
        setTranslatedMessages({});
        return;
      }

      for (const msg of selectedLead.conversation_history) {
        const msgKey = `${msg.id || msg.timestamp}_${currentLang}`;
        if (!msg.message || translatedMessages[msgKey]) continue;
        
        setIsTranslatingMessages(prev => ({ ...prev, [msg.id || msg.timestamp]: true }));
        
        try {
          const response = await axios.post(`${API}/ai/translate`, {
            text: msg.message,
            target_language: currentLang,
            context: 'conversation_message'
          });
          
          setTranslatedMessages(prev => ({
            ...prev,
            [msgKey]: response.data.translated_text
          }));
        } catch (error) {
          console.error('Translation failed for message:', msg.id);
        } finally {
          setIsTranslatingMessages(prev => ({ ...prev, [msg.id || msg.timestamp]: false }));
        }
      }
    };

    if (showDetailDialog && selectedLead) {
      translateMessages();
    }
  }, [language, selectedLead, showDetailDialog]);

  // Helper function to get translated message
  const getTranslatedMessage = (msg) => {
    const currentLang = language || 'en';
    if (currentLang === 'en') return msg.message;
    
    const msgKey = `${msg.id || msg.timestamp}_${currentLang}`;
    return translatedMessages[msgKey] || msg.message;
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="leads-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            Deal Pipeline
          </h1>
          <p className="text-muted-foreground">
            {user?.role === 'seller' 
              ? 'Manage your sales pipeline and move deals through stages'
              : 'Track your purchasing journey'}
          </p>
        </div>

        {/* Pipeline Kanban View */}
        <div className="flex gap-4 overflow-x-auto pb-4 min-h-[500px]">
          {STAGES.filter(s => !['closed_lost'].includes(s.key)).map((stage) => (
            <div 
              key={stage.key} 
              className="pipeline-column flex-shrink-0 w-72"
              data-testid={`pipeline-column-${stage.key}`}
            >
              <div className={`flex items-center justify-between mb-4 p-3 rounded-lg ${stage.bgLight}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${stage.color}`}></div>
                  <h3 className={`font-heading font-semibold ${stage.textColor}`}>{stage.label}</h3>
                </div>
                <Badge variant="secondary">{groupedLeads[stage.key]?.length || 0}</Badge>
              </div>
              
              <div className="space-y-3">
                {groupedLeads[stage.key]?.map((lead) => (
                  <Card 
                    key={lead.id} 
                    className="deal-card cursor-pointer hover:shadow-md transition-shadow border-l-4"
                    style={{ borderLeftColor: stage.color.replace('bg-', '#').replace('-500', '') }}
                    onClick={() => openLeadDetail(lead)}
                    data-testid={`lead-card-${lead.id}`}
                  >
                    <CardHeader className="pb-2 pt-3">
                      <CardTitle className="font-heading text-base flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        {lead.product_title}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {user?.role === 'buyer' ? lead.seller_name : lead.buyer_name}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pb-3">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <DollarSign className="h-3 w-3 text-green-600" />
                          <span className="font-semibold">${(lead.value || 0).toLocaleString()}</span>
                        </div>
                        {lead.quantity && (
                          <div className="text-xs text-muted-foreground">
                            Qty: {lead.quantity}
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {formatDate(lead.last_contact || lead.created_at)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {(!groupedLeads[stage.key] || groupedLeads[stage.key].length === 0) && (
                  <div className="text-center py-8 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
                    No leads in this stage
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pipeline Summary */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Pipeline Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Opportunities</p>
                <p className="text-3xl font-bold font-heading">{leads.length}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">In Negotiation</p>
                <p className="text-3xl font-bold font-heading text-amber-600">
                  {leads.filter(l => l.stage === 'negotiation').length}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Proposals Sent</p>
                <p className="text-3xl font-bold font-heading text-orange-600">
                  {leads.filter(l => l.stage === 'proposal').length}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Pipeline Value</p>
                <p className="text-3xl font-bold font-heading text-green-600">
                  ${leads.reduce((sum, lead) => sum + (lead.value || 0), 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lead Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Package className="h-5 w-5" />
                {selectedLead?.product_title}
              </DialogTitle>
              <DialogDescription>
                {user?.role === 'buyer' 
                  ? `Seller: ${selectedLead?.seller_name}` 
                  : `Buyer: ${selectedLead?.buyer_name}`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Lead Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Deal Value</p>
                  <p className="font-semibold text-lg">${(selectedLead?.value || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Quantity</p>
                  <p className="font-semibold text-lg">{selectedLead?.quantity || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">{selectedLead && formatDate(selectedLead.created_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Contact</p>
                  <p className="font-medium">{selectedLead && formatDate(selectedLead.last_contact || selectedLead.created_at)}</p>
                </div>
              </div>

              {/* Current Stage */}
              <div className={`p-3 rounded-lg ${getStageInfo(selectedLead?.stage).bgLight}`}>
                <p className="text-sm text-muted-foreground mb-1">Current Stage</p>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getStageInfo(selectedLead?.stage).color}`}></div>
                  <span className={`font-semibold ${getStageInfo(selectedLead?.stage).textColor}`}>
                    {getStageInfo(selectedLead?.stage).label}
                  </span>
                </div>
              </div>

              {/* Stage History */}
              {selectedLead?.stage_history && selectedLead.stage_history.length > 0 && (
                <div>
                  <p className="text-sm font-medium flex items-center gap-2 mb-2">
                    <History className="h-4 w-4" />
                    Stage History
                  </p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedLead.stage_history.slice().reverse().map((entry, idx) => (
                      <div key={idx} className="text-xs flex items-center gap-2 text-muted-foreground">
                        <div className={`w-2 h-2 rounded-full ${getStageInfo(entry.stage).color}`}></div>
                        <span>{getStageInfo(entry.stage).label}</span>
                        <span>•</span>
                        <span>{formatDate(entry.timestamp)}</span>
                        {entry.note && <span className="italic">- {entry.note}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Update Stage (Sellers only) */}
              {user?.role === 'seller' && (
                <div className="border-t pt-4 space-y-3">
                  <Label>Move to Stage</Label>
                  <Select 
                    value={newStage} 
                    onValueChange={(value) => {
                      setNewStage(value);
                    }}
                  >
                    <SelectTrigger data-testid="stage-select">
                      <SelectValue placeholder="Select new stage">
                        {newStage ? (
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${STAGES.find(s => s.key === newStage)?.color || ''}`}></div>
                            {STAGES.find(s => s.key === newStage)?.label || 'Select new stage'}
                          </div>
                        ) : (
                          'Select new stage'
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={5}>
                      {STAGES.map((stage) => (
                        <SelectItem 
                          key={stage.key} 
                          value={stage.key}
                          disabled={stage.key === selectedLead?.stage}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${stage.color}`}></div>
                            {stage.label}
                            {stage.key === selectedLead?.stage && (
                              <span className="text-xs text-muted-foreground ml-2">(current)</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div>
                    <Label>Note (optional)</Label>
                    <Textarea
                      placeholder="Add a note about this stage change..."
                      value={stageNote}
                      onChange={(e) => setStageNote(e.target.value)}
                      className="mt-1"
                      data-testid="stage-note-input"
                    />
                  </div>

                  <Button 
                    onClick={handleUpdateStage}
                    disabled={isUpdating || !newStage || newStage === selectedLead?.stage}
                    className="w-full"
                    data-testid="update-stage-btn"
                  >
                    {isUpdating ? 'Updating...' : (
                      <>
                        <ChevronRight className="h-4 w-4 mr-2" />
                        Update Stage
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
